/**
 * Screen Auto Lock — signs a user out after N minutes of inactivity, matching
 * ALPACA's behaviour and reading ALPACA's own setting.
 *
 * ## Why this is not just a timer
 *
 * ALPACA measures HTTP request inactivity (`Session.Timeout`). That works there
 * because every click is a postback. It does not translate to a single-page app:
 * the SPA sends a heartbeat every 60 seconds regardless of what the person is
 * doing, so "requests stopped" never happens while a tab is open. Before this,
 * Alpaca Intelligence had no idle sign-out at all — only a "the browser is gone"
 * detector — so a tab left open overnight held its concurrent-user licence until
 * the 24-hour token expired.
 *
 * The faithful equivalent in a SPA is USER inactivity: no mouse, keyboard, touch
 * or scroll. That is what this tracks.
 *
 * ## Activity is shared across tabs
 *
 * Working in one tab must keep the others alive — otherwise a second tab left on
 * a dashboard would sign the user out from under the tab they are typing in.
 * The last-activity timestamp therefore lives in `localStorage` (shared across
 * the browser profile) rather than per-tab state.
 *
 * ## Cost
 *
 * Listeners are passive and the timestamp is written at most once every
 * {@link WRITE_THROTTLE_MS}, so continuous mouse movement costs one small
 * localStorage write per few seconds, not one per event.
 */

const KEY = 'alpaca.auth.lastActivity';

/** Don't write to storage on every mousemove — once every few seconds is ample
 *  when the shortest configurable window is 30 minutes. */
const WRITE_THROTTLE_MS = 5_000;

/** Events that count as "the user is still here". Deliberately excludes
 *  `visibilitychange` and window focus: switching to another application is
 *  exactly the absence ALPACA signs you out for, so it must NOT reset the clock. */
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll', 'wheel'] as const;

let lastWrite = 0;

function now(): number { return Date.now(); }

/** Record activity, throttled. Safe when storage is unavailable. */
export function markActivity(force = false): void {
  const t = now();
  if (!force && t - lastWrite < WRITE_THROTTLE_MS) return;
  lastWrite = t;
  try { localStorage.setItem(KEY, String(t)); } catch { /* private mode — see idleMs */ }
}

/** Milliseconds since the last activity in ANY tab of this browser.
 *  Returns 0 when nothing has been recorded yet (a fresh sign-in is active). */
export function idleMs(): number {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return 0;
    const t = Number(raw);
    if (!Number.isFinite(t)) return 0;
    // A clock change (or a value from the future) must not read as "idle forever".
    return Math.max(0, now() - t);
  } catch {
    // Storage blocked: fail OPEN — never sign someone out because we cannot read
    // a timestamp. The concurrent-licence sweep still reclaims an abandoned
    // browser, so the licence is not stranded either way.
    return 0;
  }
}

/** Has the user been inactive longer than the configured window?
 *  `minutes` null/0 means the feature is off (ALPACA has no row). */
export function isIdleBeyond(minutes: number | null | undefined): boolean {
  if (!minutes || minutes <= 0) return false;
  return idleMs() >= minutes * 60_000;
}

/** Clear the timestamp — on sign-in (start the clock fresh) and on sign-out
 *  (so a stale value cannot immediately sign the next user out). */
export function resetActivity(): void {
  lastWrite = 0;
  try { localStorage.setItem(KEY, String(now())); } catch { /* ignore */ }
}

/**
 * Start listening. Returns an unsubscribe function.
 *
 * `onIdle` fires ONCE when the window is first exceeded; the caller is expected
 * to sign the user out. Checked on a timer rather than per-event because the
 * interesting moment is the ABSENCE of events.
 */
export function watchIdle(
  getMinutes: () => number | null | undefined,
  onIdle: () => void,
  checkEveryMs = 15_000,
): () => void {
  let fired = false;
  const onEvent = () => { markActivity(); };

  for (const e of ACTIVITY_EVENTS) {
    window.addEventListener(e, onEvent, { passive: true });
  }
  markActivity(true);   // signing in counts as activity

  const timer = setInterval(() => {
    if (fired) return;
    if (isIdleBeyond(getMinutes())) { fired = true; onIdle(); }
  }, checkEveryMs);

  return () => {
    clearInterval(timer);
    for (const e of ACTIVITY_EVENTS) window.removeEventListener(e, onEvent);
  };
}
